import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  PenLine,
  Heart,
  Sparkles,
  Users,
  ShieldCheck,
  GraduationCap,
  HeartHandshake,
  ChevronRight,
  ArrowRight,
} from "lucide-react";

const STEPS = [
  {
    icon: PenLine,
    title: "커뮤니티 공간에 나의 일상과 관심사를 나눕니다",
    desc: "꾸미지 않은 진짜 나를 자연스럽게 보여줍니다",
  },
  {
    icon: Heart,
    title: "비슷한 가치관의 사람들과 연결됩니다",
    desc: "공감과 대화를 통해 편안한 관계가 시작됩니다",
  },
  {
    icon: Sparkles,
    title: "AI와 커뮤니티가 좋은 연결을 도와줍니다",
    desc: "당신의 글을 기반으로 잘 맞는 사람을 더 정교하게 추천해줍니다",
  },
  {
    icon: Users,
    title: "커뮤니티와 오프모임 속에서 가까워집니다",
    desc: "다양한 활동과 만남 속에서 자연스럽게 친해집니다",
  },
  {
    icon: ShieldCheck,
    title: "검증된 회원들과 대화 나눌 수 있는 기회를 갖게 됩니다",
    desc: "정회원 이상은 직접 프로필을 보며 나와 맞는 사람을 선택할 수 있습니다",
  },
  {
    icon: GraduationCap,
    title: "코칭과 연애사관학교를 통해 관계를 배웁니다",
    desc: "사랑과 소통, 관계의 기술을 함께 성장시켜갑니다",
  },
  {
    icon: HeartHandshake,
    title: "그렇게 좋은 인연을 만나게 됩니다",
    desc: "친구처럼 시작해 인생의 소중한 관계로 이어집니다",
  },
];

export const SevenStepsSection = () => {
  return (
    <section
      id="seven-steps"
      className="relative py-20 lg:py-28 overflow-hidden bg-gradient-to-b from-[#FCEAEC] via-[#FBF1F0] to-[#FAF6F0]"
      data-testid="seven-steps-section"
    >
      {/* Decorative hearts */}
      <Heart
        className="absolute top-16 left-[6%] w-16 h-16 text-burgundy-200/40"
        fill="currentColor"
        aria-hidden
      />
      <Heart
        className="absolute top-24 left-[12%] w-8 h-8 text-burgundy-200/30"
        fill="currentColor"
        aria-hidden
      />
      <Heart
        className="absolute top-14 right-[8%] w-20 h-20 text-burgundy-200/30"
        strokeWidth={1}
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-14 lg:mb-20"
        >
          <div className="text-[11px] sm:text-[13px] tracking-[0.3em] font-semibold text-gold-500 uppercase mb-4">
            The Process of Meaningful Connection
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-stone-900 leading-[1.2] tracking-tight">
            좋은 인연을 만나는 7단계
          </h2>
          <p className="text-stone-500 text-[15px] sm:text-base leading-[1.9] mt-6">
            뉴커플링은 억지스러운 소개가 아니라,
            <br />
            자연스러운 공감과 관계의 성장을 통해 좋은 인연으로 이어지도록 돕습니다.
          </p>
        </motion.div>

        {/* Horizontal step cards */}
        <div className="flex items-stretch gap-3 lg:gap-1 overflow-x-auto lg:overflow-visible pt-8 pb-4 -mx-5 px-5 lg:mx-0 lg:px-0 snap-x snap-mandatory lg:snap-none [scrollbar-width:none] [-ms-overflow-style:none]">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            const isLast = i === STEPS.length - 1;
            return (
              <div key={i} className="flex items-stretch shrink-0 lg:flex-1">
                {/* Card */}
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  className="relative w-[230px] sm:w-[250px] lg:w-auto lg:flex-1 snap-center"
                  data-testid={`seven-steps-card-${i + 1}`}
                >
                  {/* Number badge */}
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-10 w-11 h-11 rounded-full bg-gradient-to-br from-stone-400 via-stone-500 to-stone-700 text-white flex items-center justify-center text-base font-bold shadow-md shadow-stone-500/30 ring-4 ring-[#FCEAEC]">
                    {i + 1}
                  </div>

                  <div className="h-full bg-white/90 backdrop-blur-sm rounded-[1.75rem] pt-12 pb-8 px-5 text-center shadow-[0_10px_30px_rgb(120,30,50,0.06)] border border-rose-100/80 hover:-translate-y-1.5 hover:shadow-[0_16px_40px_rgb(120,30,50,0.12)] transition-all duration-300">
                    <div className="w-20 h-20 mx-auto rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center mb-6">
                      <Icon className="w-9 h-9 text-burgundy-400" strokeWidth={1.6} />
                    </div>
                    <h3 className="font-bold text-stone-800 text-[15px] leading-snug mb-3 min-h-[3.2em] flex items-center justify-center">
                      {step.title}
                    </h3>
                    <p className="text-stone-500 text-[12.5px] leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </motion.div>

                {/* Chevron connector */}
                {!isLast && (
                  <div className="flex items-center px-1 lg:px-0.5 shrink-0">
                    <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6 text-rose-300" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="text-center mt-16 lg:mt-20"
        >
          <Link
            to="/community"
            className="inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full bg-gradient-to-r from-burgundy-400 via-burgundy-600 to-burgundy-800 text-white font-bold text-[15px] shadow-lg shadow-burgundy-600/25 hover:shadow-xl hover:shadow-burgundy-600/30 hover:gap-3 transition-all duration-300"
            data-testid="seven-steps-cta"
          >
            뉴커플링 시작하기
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default SevenStepsSection;
